import { Controller } from "@hotwired/stimulus"

// Collapses the desktop sidebar to its icon rail and back.
//
// The state is one attribute, data-sidebar="collapsed" on <html>. Turbo Drive
// swaps <body> but keeps <html>, so the state survives navigation; a cookie
// carries it to the server, which renders the attribute on a full load so the
// wide sidebar never flashes first. Every width that depends on the sidebar
// reads --app-sidebar-width (components/sidebar_rail.css), so nothing here
// measures or moves anything.
//
// "[" toggles it from anywhere outside a text field.
const COOKIE = "sidebar_collapsed"
const DESKTOP = "(min-width: 1280px)"

export default class extends Controller {
  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    this.onTransitionEnd = this.onTransitionEnd.bind(this)
    document.addEventListener("keydown", this.onKeydown)
    this.element.addEventListener("transitionend", this.onTransitionEnd)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
    this.element.removeEventListener("transitionend", this.onTransitionEnd)
  }

  collapse() {
    this.apply(true)
  }

  expand() {
    this.apply(false)
  }

  toggle() {
    this.apply(!this.collapsed)
  }

  get collapsed() {
    return document.documentElement.dataset.sidebar === "collapsed"
  }

  apply(collapsed) {
    if (collapsed === this.collapsed) return

    if (collapsed) {
      document.documentElement.dataset.sidebar = "collapsed"
    } else {
      delete document.documentElement.dataset.sidebar
    }

    const secure = window.location.protocol === "https:" ? "; secure" : ""
    const lifetime = collapsed ? "; max-age=31536000" : "; max-age=0"
    document.cookie = `${COOKIE}=${collapsed ? "1" : ""}; path=/; samesite=lax${lifetime}${secure}`

    // With no width transition to wait for, tell the window-resize listeners now.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) this.announceResize()
  }

  // Tables, viewers and the activity stream that listen to window resize would
  // otherwise miss the content column growing or shrinking, since the window
  // itself did not change. ResizeObserver-based controllers follow on their own.
  onTransitionEnd(event) {
    if (event.target !== this.element || event.propertyName !== "width") return
    this.announceResize()
  }

  announceResize() {
    window.dispatchEvent(new Event("resize"))
  }

  onKeydown(event) {
    if (event.key !== "[" || event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return
    if (this.isEditable(event.target)) return
    if (!window.matchMedia(DESKTOP).matches) return
    // Board focus mode hides the sidebar outright; there is nothing to toggle.
    if (this.element.getClientRects().length === 0) return

    event.preventDefault()
    this.toggle()
  }

  isEditable(element) {
    if (!(element instanceof Element)) return false
    return element.isContentEditable || element.closest("input, textarea, select, [contenteditable]") !== null
  }
}
